import Asset, {AssetCategory, AssetType, getAssetTypeDef} from "../../../model/Asset";
import {Box, Chip, Grid, Tooltip, Typography} from "@mui/material";
import ChatbotOpenImg from "../../../images/ChatbotOpen.png";
import * as React from "react";
import AssetEditDialog from "./AssetEditDialog";
import Model from "../../../model/Model";
import Snapshot, {ISnapshotDetail} from "../../../model/Snapshot";
import IModelItem from "../../../model/IModelItem";
import {createUUID, getISODateStartOfYear, getISODateToday} from "../../../stores/StoreUtilities";
import ModelStore from "../../../stores/ModelStore";
import DialogButton from "../../../components/form/DialogButton";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {liabilityMenuOptions, NetWorthSection, realEstateAndPropertyMenuOptions} from "./NetWorthView";
import Liability from "../../../model/Liability";
import {useState} from "react";
import LiabilityEditDialog from "./LiabilityEditDialog";
import UserSetting, {IQuickStartSettings} from "../../../model/UserSetting";
import {LiabilityIcon, LiquidAssetIcon, RealAssetIcon} from "../../../styles/Icons";
import globalStyles from "../../../styles/globalStyles";
import QuickStartWidget from "../dashboard/widgets/QuickStartWidget";

const NetWorthQuickStart = ({
  step,
  model,
  snapshot,
  quickStartUserSetting,
  onSave,
  onNext,
  onExit
}: {
  step?: string
  model: Model
  snapshot?: Snapshot
  quickStartUserSetting?: UserSetting
  onSave(item: IModelItem, snapshotItem?: ISnapshotDetail): void
  onNext(settings: IQuickStartSettings): void
  onExit(settings: IQuickStartSettings): void
}) => {
  const [asset, setAsset] = useState<Asset | undefined>()
  const [liability, setLiability] = useState<Liability | undefined>()

  const styles = {
    finn: {
      fontStyle:"italic"
    },
    chip: {
      mr: 1,
      my: 0.5,
      pl: 1,
      cursor: "pointer",
      borderRadius: "5px",
      boxShadow: "rgba(0, 0, 0, 0.15) 0px 3px 3px 0px"
    }
  }

  const handleSave = (item: IModelItem, snapshotItem?: ISnapshotDetail) => {
    onSave(item, snapshotItem)
    setAsset(undefined)
    setLiability(undefined)
  }

  const handlePrior = (event: any) => {
    let nextStep = ""
    let nextView = "networth"
    switch (step) {
      case NetWorthSection.LiquidAssets:
        nextView = "timeline"
        nextStep = "People"
        break
      case NetWorthSection.RealEstateAssets:
        nextStep = NetWorthSection.LiquidAssets
        break
      case NetWorthSection.PersonalPropertyAssets:
        nextStep = NetWorthSection.RealEstateAssets
        break
      case NetWorthSection.OtherAssets:
        nextStep = NetWorthSection.PersonalPropertyAssets
        break
      case NetWorthSection.Liabilities:
        nextStep = NetWorthSection.RealEstateAssets
        break
    }
    if (nextStep) {
      onNext({status: "Active", view: nextView, step: nextStep})
    }
  }

  const handleNext = (event: any) => {
    let nextStep = ""
    let nextView = "networth"
    switch (step) {
      case NetWorthSection.LiquidAssets:
        nextStep = NetWorthSection.RealEstateAssets
        break
      case NetWorthSection.RealEstateAssets:
        nextStep = NetWorthSection.Liabilities
        break
      case NetWorthSection.PersonalPropertyAssets:
        nextStep = NetWorthSection.OtherAssets
        break
      case NetWorthSection.OtherAssets:
        nextStep = NetWorthSection.Liabilities
        break
      case NetWorthSection.Liabilities:
        nextView = "budget"
        nextStep = "Start"
        break
    }
    if (nextStep) {
      onNext({status: "Active", view: nextView, step: nextStep})
    }
  }

  // const liquidAssetMenuOptions = listAssetTypeDefs().map((def: AssetTypeDef) => {
  //   return new Asset({
  //     description: def.label,
  //     assetCategory: AssetCategory.LiquidInvestableAssets,
  //     assetType: def.assetType
  //   })
  // })


  const renderLiquidAssetChips = () => {
    const assetOptions = [AssetType.Checking, AssetType.Savings, AssetType.MoneyMarket, AssetType.Brokerage, AssetType.Plan401k, AssetType.RothIRA, AssetType.TraditionalIRA, AssetType.HSA, AssetType.None]

    return (
      assetOptions.map((assetType: AssetType) => {
          const typedef = getAssetTypeDef(assetType)
          if (typedef) {
            return (
              <Tooltip title={<Typography variant="body2" sx={{lineHeight: 1.3}}>{typedef.description}</Typography>} arrow
                       placement="top-end" enterDelay={200}>
                <Chip key={typedef.assetType}
                      variant="outlined"
                      icon={<LiquidAssetIcon/>}
                      label={typedef.label}
                      sx={styles.chip}
                      clickable
                      onClick={() => setAsset(new Asset({
                        id: createUUID(),
                        accountId: model.accountId,
                        userId: model.userId,
                        modelId: model.id,
                        assetCategory: AssetCategory.LiquidInvestableAssets,
                        assetType: typedef.assetType,
                        description: typedef.label,
                        balance: 0,
                        balanceDate: snapshot?.date,
                        start: getISODateStartOfYear(),
                        sortOrder: model.assets.length > 0 ? model.assets[model.assets.length - 1].sortOrder + ModelStore.orderInterval : 0
                      }))}
                />
              </Tooltip>
            )
          } else {
            return null
          }
        }
      )
    )
  }

  const renderRealEstateAssetChips = () => {
    const filteredOptions = realEstateAndPropertyMenuOptions.filter((option: Asset) => model.assets.find((a: Asset) => a.description === option.description) === undefined)

    return (
      filteredOptions.map((asset: Asset) => {
          return (
            <Chip key={asset.description}
                  variant="outlined"
                  label={asset.description}
                  icon={<RealAssetIcon/>}
                  sx={styles.chip}
                  clickable
                  onClick={() => setAsset(new Asset({
                    id: createUUID(),
                    accountId: model.accountId,
                    userId: model.userId,
                    modelId: model.id,
                    description: asset.description,
                    balance: 0,
                    balanceDate: snapshot?.date ?? getISODateToday(),
                    assetCategory: asset.assetCategory,
                    assetType: asset.assetType,
                    start: getISODateStartOfYear(),
                    archived: false,
                    sortOrder: model.assets.length > 0 ? model.assets[model.assets.length-1].sortOrder + ModelStore.orderInterval : 0
                  }))}
            />
        )
      })
    )
  }

  const renderLiabilityChips = () => {
    const options = liabilityMenuOptions

    return (
      options.map((option: string) => {
        return (
          <Chip key={option}
              variant="outlined"
              label={option}
              icon={<LiabilityIcon/>}
              sx={styles.chip}
              clickable
              onClick={() => setLiability(new Liability({
                id: createUUID(),
                accountId: model.accountId,
                userId: model.userId,
                modelId: model.id,
                description: option,
                balance: 0,
                balanceDate: snapshot?.date,
                start: getISODateStartOfYear(),
                sortOrder: model.liabilities.length > 0 ? model.liabilities[model.liabilities.length - 1].sortOrder + ModelStore.orderInterval : 0
              }))}
          />
        )
      })
    )
  }

  if (step === NetWorthSection.LiquidAssets) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Let's start with your liquid asset accounts, such as checking, savings, money market, CD, IRA and 401k
              accounts. Please click on an account type or Other. When you're done, click Next Step.
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>Liquid Assets</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!asset && renderLiquidAssetChips()}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px"}}>
          {asset &&
            <AssetEditDialog asset={asset} model={model} snapshot={snapshot} open
                             onSave={handleSave}
                             onClose={() => setAsset(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!asset &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === NetWorthSection.RealEstateAssets) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add your real estate assets, such as your primary residence, second home, or rental property. Please click a type or Other Property. When you're done, click Next Step.
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>Real Estate</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!asset && renderRealEstateAssetChips()}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px"}}>
          {asset &&
            <AssetEditDialog asset={asset} model={model} snapshot={snapshot} open
                             onSave={handleSave}
                             onClose={() => setAsset(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!asset &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === NetWorthSection.OtherAssets) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              We will skip Personal Property and Other Assets for now, but you can add them later. Press Next Step to continue.
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>Personal Property & Other Assets</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "48px"}}>
          {!asset &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else if (step === NetWorthSection.Liabilities) {
    return (
      <Grid container spacing={1} sx={{mt: 0, px: 1}}>
        <Grid item xs={12}>
          <QuickStartWidget model={model} quickStartUserSetting={quickStartUserSetting} onExit={onExit}/>
        </Grid>
        <Grid item xs={12} display="flex" alignItems="flex-start">
          <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
          <Box pt={0}>
            <Typography variant="body2" sx={styles.finn} gutterBottom>
              Next add any liabilities such as loans, credit cards, or HELOCs. Please click a type or Other Liability. When you're done, click Next Step.
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>Liabilities</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ml: "44px"}}>
          {!liability && renderLiabilityChips()}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ml: "44px"}}>
          {liability &&
            <LiabilityEditDialog liability={liability} model={model} snapshot={snapshot} open
                             onSave={handleSave}
                             onClose={() => setLiability(undefined)} quickForm/>
          }
        </Grid>
        <Grid item xs={12} sx={{ml: "44px", mr:1}}>
          {!liability &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box>
                <DialogButton variant="primary" size="medium" onClick={handlePrior} type="button" customClassName={globalStyles.prevButton} spacing="right">
                  <ChevronLeftIcon/> Prev Step
                </DialogButton>
                <DialogButton variant="primary" size="medium" onClick={handleNext} type="button" customClassName={globalStyles.nextButton}>
                  Next Step <ChevronRightIcon/>
                </DialogButton>
              </Box>
            </Box>
          }
        </Grid>
      </Grid>
    )
  } else {
    return <></>
  }
}

export default NetWorthQuickStart